import React, { useState, useEffect, useContext, useRef } from "react"
import moment from "moment-timezone"
import { ShieldCheck, CheckCircle2, ArrowRight, ChevronDown, CreditCard, Share2, Copy, Check, Mail, Send } from "lucide-react"

import { BookingContext } from "../../../utils/providers/isolated-booking"
import { db, funEU } from "../../../utils/firebase"
import CustomerPay from "../../../components/payments/customer-pay/customer-pay"
import ATOLLogo from "../../../assets/images/atol-protection.png"

import styles from "./payments.module.scss"

const sendInstructionsCallable = funEU.httpsCallable("sendPaymentInstructions")

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

/**
 * Payments — reskinned onto the portal design system. The Stripe customer
 * payment flow (CustomerPay) is reused unchanged; only the surrounding shell,
 * amount/details inputs and history list are rebuilt.
 *
 * Payment dates are formatted in Europe/London (portal root sets the default).
 */
export default function Payments() {
	const { bookingID, reference, outstanding, totalCost, paidByClient, client } = useContext(BookingContext)

	const [payments, setPayments] = useState([])
	const [paymentAmount, setPaymentAmount] = useState("")
	const [selectedPaymentOption, setSelectedPaymentOption] = useState("PARTIAL")
	const [amountError, setAmountError] = useState("")
	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")
	const [email, setEmail] = useState("")
	const [showPaymentForm, setShowPaymentForm] = useState(false)
	const payFormRef = useRef(null)

	// Both action cards start collapsed; the client taps to open them.
	const [makeOpen, setMakeOpen] = useState(false)
	const [instructionsOpen, setInstructionsOpen] = useState(false)
	const [copied, setCopied] = useState(false)

	// Email-the-instructions form.
	const [emailMode, setEmailMode] = useState(false)
	const [recipientEmail, setRecipientEmail] = useState("")
	const [sending, setSending] = useState(false)
	const [sendError, setSendError] = useState("")
	const [sentTo, setSentTo] = useState("")

	const total = Number(totalCost || 0)
	const paid = Number(paidByClient || 0)
	const owed = Number(outstanding || 0)
	const paidPct = total > 0 ? Math.min(100, Math.max(0, Math.round((paid / total) * 100))) : 0
	const settled = owed <= 0 && total > 0

	const formattedOutstanding = formatGbp(owed)

	// When "Full balance" is selected, keep the amount synced to outstanding.
	useEffect(() => {
		if (selectedPaymentOption === "FULL") {
			setPaymentAmount(owed.toFixed(2))
		}
	}, [selectedPaymentOption, owed])

	// Validate partial payments against the outstanding balance.
	useEffect(() => {
		if (selectedPaymentOption !== "PARTIAL") {
			setAmountError("")
			return
		}
		const numericAmount = parseFloat((paymentAmount || "").replace(/[^0-9.]/g, "")) || 0
		if (numericAmount > owed) {
			setAmountError(`Amount exceeds outstanding balance (£${formattedOutstanding})`)
		} else {
			setAmountError("")
		}
	}, [paymentAmount, selectedPaymentOption, owed, formattedOutstanding])

	// When the payment method step swaps in it's shorter than the details form,
	// so the card/bank options can land above the fold (especially on mobile,
	// where the client was scrolled down by the "Continue" button). Bring the
	// form back into view so the options are visible without scrolling up.
	useEffect(() => {
		if (!showPaymentForm || !payFormRef.current) return
		const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
		payFormRef.current.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "start" })
	}, [showPaymentForm])

	// Live payment history.
	useEffect(() => {
		if (!bookingID) return
		setPayments([])

		const unsubscribe = db
			.collection(`bookings/${bookingID}/payments`)
			.orderBy("paid_date", "desc")
			.onSnapshot(
				(snapshot) => {
					setPayments(
						snapshot.docs.map((paymentDoc) => {
							const { amount, paid_date, payee, reference, status } = paymentDoc.data()
							return {
								id: paymentDoc.id,
								amount: Number(amount || 0),
								payee,
								reference,
								status,
								paidDateReadable: paid_date?.seconds ? moment(paid_date.seconds, "X").format("DD MMM YYYY, HH:mm") : "",
							}
						}),
					)
				},
				(error) => console.log("Payments listener error:", error),
			)

		return () => unsubscribe()
	}, [bookingID])

	const normaliseAmount = () => {
		let formattedAmount = (paymentAmount || "").replace(/[^0-9.]/g, "")
		if (!formattedAmount.includes(".")) formattedAmount += ".00"
		if (formattedAmount.split(".")[1]?.length > 2) {
			formattedAmount = formattedAmount.split(".")[0] + "." + formattedAmount.split(".")[1].slice(0, 2)
		}
		if (formattedAmount.split(".")[1]?.length === 1) formattedAmount += "0"
		formattedAmount = formattedAmount.replace(/^0+(?=\d)/, "")
		return Number(formattedAmount)
	}

	const startPayment = () => {
		const numericAmount = parseFloat((paymentAmount || "").replace(/[^0-9.]/g, "")) || 0
		if (numericAmount > owed) {
			setAmountError(`Amount exceeds outstanding balance (£${formattedOutstanding})`)
			return
		}
		setShowPaymentForm(true)
	}

	const canStartPayment = !amountError && (parseFloat((paymentAmount || "").replace(/[^0-9.]/g, "")) || 0) > 0 && firstName.trim() && lastName.trim() && email.trim()

	const returnUrl = `${window.location.origin}${window.location.pathname.replace(/\/payments$/, "/payment")}`

	// Shareable instructions so anyone on the booking can pay their share.
	const loginUrl = "https://www.ghdsystem.com/mybooking"
	const leadBookerEmail = client?.email
	const step2 = leadBookerEmail
		? `2. Sign in using the lead booker's email address (${leadBookerEmail}) and the booking reference (${reference}).`
		: `2. Sign in using the lead booker's email address and the booking reference (${reference}).`

	const instructionsText = `Hello,

You've been requested to make a payment towards an upcoming booking with Golf Holidays Direct, booking reference #${reference}.

Please follow the steps below to make a payment towards the booking:

1. Visit this link: ${loginUrl}
${step2}
3. Go to the Payments tab.
4. Tap "Make a payment", enter your details, and continue.

Thank you.`

	const copyInstructions = async () => {
		try {
			await navigator.clipboard.writeText(instructionsText)
			setCopied(true)
			setTimeout(() => setCopied(false), 2200)
		} catch (err) {
			console.log("Clipboard copy failed:", err)
		}
	}

	const sendInstructionsEmail = async () => {
		const to = recipientEmail.trim()
		if (!EMAIL_RE.test(to)) {
			setSendError("Please enter a valid email address.")
			return
		}
		setSending(true)
		setSendError("")
		try {
			await sendInstructionsCallable({ bookingID, recipientEmail: to })
			setSentTo(to)
			setRecipientEmail("")
		} catch (err) {
			setSendError(err?.message || "Could not send the email. Please try again.")
		} finally {
			setSending(false)
		}
	}

	return (
		<div className={styles.payments}>
			<header className={styles.head}>
				<h1>Payments</h1>
				<p>Pay towards your golf holiday any time. Use your booking reference on any external transfers.</p>
			</header>

			{/* Balance summary */}
			<section className={styles.balance}>
				<div className={styles.balanceTop}>
					<div>
						<span className={styles.balanceLabel}>{settled ? "Balance" : "Outstanding balance"}</span>
						<span className={styles.balanceBig}>£{formattedOutstanding}</span>
					</div>
					{settled ? (
						<span className={styles.paidBadge}>
							<CheckCircle2 size={15} />
							Paid in full
						</span>
					) : null}
				</div>

				<div className={styles.progress}>
					<div
						className={styles.progressFill}
						style={{ width: `${paidPct}%` }}
					/>
				</div>

				<div className={styles.balanceMeta}>
					<span>£{formatGbp(paid)} paid</span>
					<span>£{formatGbp(total)} total</span>
				</div>
			</section>

			{/* Make a payment (collapsible) */}
			{!settled && (
				<section className={styles.card}>
					<button
						type="button"
						className={styles.cardToggle}
						aria-expanded={makeOpen}
						onClick={() => setMakeOpen((open) => !open)}>
						<span className={styles.cardToggleText}>
							<CreditCard size={18} />
							Make a payment
						</span>
						<ChevronDown
							size={20}
							className={`${styles.chevron} ${makeOpen ? styles.chevronOpen : ""}`}
						/>
					</button>

					{makeOpen && (
						<div className={styles.cardBody}>
							{!showPaymentForm ? (
								<>
									<div className={styles.options}>
										<button
											type="button"
											className={`${styles.option} ${selectedPaymentOption === "PARTIAL" ? styles.optionActive : ""}`}
											onClick={() => setSelectedPaymentOption("PARTIAL")}>
											<strong>Part payment</strong>
											<span>Choose a custom amount</span>
										</button>
										<button
											type="button"
											className={`${styles.option} ${selectedPaymentOption === "FULL" ? styles.optionActive : ""}`}
											onClick={() => setSelectedPaymentOption("FULL")}>
											<strong>Full balance</strong>
											<span>Pay the remaining £{formattedOutstanding}</span>
										</button>
									</div>

									{selectedPaymentOption === "PARTIAL" && (
										<label className={styles.field}>
											<span className={styles.label}>Amount</span>
											<div className={styles.amountWrap}>
												<span className={styles.currency}>£</span>
												<input
													type="text"
													inputMode="decimal"
													placeholder="0.00"
													value={paymentAmount}
													onChange={(e) => setPaymentAmount(e.target.value)}
												/>
											</div>
											{amountError ? <span className={styles.fieldError}>{amountError}</span> : null}
										</label>
									)}

									<div className={styles.fieldRow}>
										<label className={styles.field}>
											<span className={styles.label}>First name</span>
											<input
												type="text"
												value={firstName}
												onChange={(e) => setFirstName(e.target.value)}
											/>
										</label>
										<label className={styles.field}>
											<span className={styles.label}>Last name</span>
											<input
												type="text"
												value={lastName}
												onChange={(e) => setLastName(e.target.value)}
											/>
										</label>
									</div>

									<label className={styles.field}>
										<span className={styles.label}>Email address</span>
										<input
											type="email"
											value={email}
											onChange={(e) => setEmail(e.target.value)}
										/>
									</label>

									<button
										type="button"
										className={styles.continue}
										onClick={startPayment}
										disabled={!canStartPayment}>
										Continue to payment
										<ArrowRight size={17} />
									</button>

									<p className={styles.hint}>You can choose to pay by card or bank transfer on the next step.</p>
								</>
							) : (
								<div
									className={styles.payForm}
									ref={payFormRef}>
									<button
										type="button"
										className={styles.back}
										onClick={() => setShowPaymentForm(false)}>
										← Edit amount or details
									</button>
									<CustomerPay
										bookingID={bookingID}
										amount={normaliseAmount()}
										payerFirstName={firstName.trim()}
										payerLastName={lastName.trim()}
										payerEmail={email.trim()}
										returnUrl={returnUrl}
									/>
								</div>
							)}
						</div>
					)}
				</section>
			)}

			{/* Send payment instructions (collapsible) */}
			<section className={styles.card}>
				<button
					type="button"
					className={styles.cardToggle}
					aria-expanded={instructionsOpen}
					onClick={() => setInstructionsOpen((open) => !open)}>
					<span className={styles.cardToggleText}>
						<Share2 size={18} />
						Send payment instructions
					</span>
					<ChevronDown
						size={20}
						className={`${styles.chevron} ${instructionsOpen ? styles.chevronOpen : ""}`}
					/>
				</button>

				{instructionsOpen && (
					<div className={styles.cardBody}>
						<p className={styles.instrIntro}>Sharing the cost with your group? Send these instructions to anyone who needs to pay towards the booking.</p>

						<pre className={styles.instrMessage}>{instructionsText}</pre>

						<div className={styles.instrActions}>
							<button
								type="button"
								className={styles.copyButton}
								onClick={copyInstructions}>
								{copied ? <Check size={16} /> : <Copy size={16} />}
								{copied ? "Copied to clipboard" : "Copy instructions"}
							</button>
							{!emailMode && (
								<button
									type="button"
									className={styles.emailButton}
									onClick={() => {
										setEmailMode(true)
										setSentTo("")
										setSendError("")
									}}>
									<Mail size={16} />
									Send via email
								</button>
							)}
						</div>

						{emailMode && (
							<div className={styles.emailForm}>
								{sentTo ? (
									<div className={styles.emailSuccess}>
										<span className={styles.emailSuccessIcon}>
											<Check size={22} />
										</span>
										<p>
											Instructions sent to <strong>{sentTo}</strong>
										</p>
										<button
											type="button"
											className={styles.emailAnother}
											onClick={() => setSentTo("")}>
											Send to someone else
										</button>
									</div>
								) : (
									<>
										<label className={styles.field}>
											<span className={styles.label}>Recipient email address</span>
											<input
												type="email"
												placeholder="name@example.com"
												value={recipientEmail}
												onChange={(e) => setRecipientEmail(e.target.value)}
											/>
											{sendError ? <span className={styles.fieldError}>{sendError}</span> : null}
										</label>
										<div className={styles.emailFormActions}>
											<button
												type="button"
												className={styles.sendButton}
												onClick={sendInstructionsEmail}
												disabled={sending || !recipientEmail.trim()}>
												<Send size={15} />
												{sending ? "Sending…" : "Send email"}
											</button>
											<button
												type="button"
												className={styles.cancelButton}
												onClick={() => {
													setEmailMode(false)
													setSendError("")
												}}>
												Cancel
											</button>
										</div>
									</>
								)}
							</div>
						)}
					</div>
				)}
			</section>

			{/* Payment history */}
			<section className={styles.card}>
				<h2>Payment history</h2>
				{payments.length === 0 ? (
					<p className={styles.empty}>No recorded payments yet. If you've paid recently, please allow up to 24 hours for it to appear here.</p>
				) : (
					<ul className={styles.history}>
						{payments.map((payment) => (
							<li key={payment.id}>
								<div className={styles.historyMain}>
									<span className={styles.historyPayee}>
										{payment.payee}
										{payment.status === "Pending" ? <span className={styles.pending}>Pending</span> : null}
									</span>
									<span className={styles.historyDate}>{payment.paidDateReadable}</span>
								</div>
								<div className={styles.historyRight}>
									<span className={`${styles.historyAmount} ${payment.amount < 0 ? styles.refund : ""}`}>
										{payment.amount < 0 ? "-" : ""}£{formatGbp(Math.abs(payment.amount))}
									</span>
									{payment.reference ? <span className={styles.historyRef}>{payment.reference}</span> : null}
								</div>
							</li>
						))}
					</ul>
				)}
			</section>

			{/* Financial protection */}
			<section className={styles.protection}>
				<div className={styles.protectionText}>
					<span className={styles.protectionHead}>
						<ShieldCheck size={17} />
						Your money is protected
					</span>
					<p>Golf Holidays Direct holds an ATOL licence (11357) and keeps client money in an independently managed trust account, giving you the highest level of financial protection in the industry.</p>
				</div>
				<img
					src={ATOLLogo}
					alt="ATOL protected"
					className={styles.atol}
				/>
			</section>
		</div>
	)
}
